import { addDays, format, parseISO } from 'date-fns';

/**
 * Editing shapes for a Source's dated charging rules.
 *
 * The stored form keeps per-order and recurring charges in one list; the
 * editor keeps them apart, because only the per-order ones have an order that
 * means anything. A recurring charge is independent of its neighbours, so
 * letting someone drag it up and down would imply a significance it lacks.
 *
 * No arithmetic lives here. What a rule costs is answered by the server, so
 * the editor's preview and the dashboard cannot disagree.
 */

let sequence = 0;
const nextKey = () => `dr-${++sequence}`;

export const newPerOrderRule = () => ({ key: nextKey(), charge: 'flat', amount: '', basis: 'running', label: '' });
export const newPeriodRule = () => ({ key: nextKey(), amount: '', every_days: '30', label: '' });
export const newRuleSet = (startDate = '') => ({
  key: nextKey(), start_date: startDate, perOrder: [], period: [],
});

export const toEditor = (sets) => (Array.isArray(sets) ? sets : []).map(set => ({
  key: nextKey(),
  start_date: set?.start_date || '',
  perOrder: (set?.rules || []).filter(rule => rule?.kind === 'per_order').map(rule => ({
    key: nextKey(),
    charge: rule.charge === 'percent' ? 'percent' : 'flat',
    amount: String(rule.charge === 'percent' ? rule.percent ?? '' : rule.amount ?? ''),
    basis: rule.basis || 'running',
    label: String(rule.label ?? ''),
  })),
  period: (set?.rules || []).filter(rule => rule?.kind === 'period').map(rule => ({
    key: nextKey(),
    amount: String(rule.amount ?? ''),
    every_days: String(rule.every_days ?? ''),
    label: String(rule.label ?? ''),
  })),
}));

const trimmed = value => String(value ?? '').trim();

// A blank name is left out rather than sent as an empty string, so an unnamed
// charge stores nothing and reads back as unnamed.
const named = (row, rule) => (trimmed(rule.label) ? { ...row, label: trimmed(rule.label) } : row);

export const toPayload = (sets) => sets.map(set => ({
  start_date: trimmed(set.start_date),
  rules: [
    ...set.perOrder.map(rule => named(rule.charge === 'percent'
      ? { kind: 'per_order', charge: 'percent', percent: trimmed(rule.amount), basis: rule.basis }
      : { kind: 'per_order', charge: 'flat', amount: trimmed(rule.amount) }, rule)),
    ...set.period.map(rule => named({
      kind: 'period',
      charge: 'flat',
      amount: trimmed(rule.amount),
      // An empty box becomes 0, which the server refuses as surely as any
      // other non-positive value — so a blank cannot slip through as a
      // silently valid cycle. localProblems catches it first with wording a
      // person can act on.
      every_days: Number(trimmed(rule.every_days)),
    }, rule)),
  ],
}));

const readable = iso => {
  try {
    return format(parseISO(iso), 'd MMM yyyy');
  } catch {
    return iso;
  }
};

/** Sets in date order, each with the end its successor implies. */
export const withWindows = (sets) => {
  const ordered = [...sets].sort((a, b) => trimmed(a.start_date).localeCompare(trimmed(b.start_date)));
  return ordered.map((set, index) => {
    const next = ordered[index + 1];
    let endsOn = null;
    if (next && trimmed(next.start_date)) {
      try {
        endsOn = format(addDays(parseISO(trimmed(next.start_date)), -1), 'yyyy-MM-dd');
      } catch {
        endsOn = null;
      }
    }
    return { ...set, endsOn, isCurrent: !next };
  });
};

/**
 * What deleting a set will actually do, in the owner's terms.
 *
 * Removing one from the middle of the timeline leaves no gap — the set before
 * it stretches forward to cover the window. That re-prices real months, so it
 * is said plainly before it happens rather than discovered afterwards.
 */
export const describeDeletion = (sets, key) => {
  const ordered = withWindows(sets);
  const index = ordered.findIndex(set => set.key === key);
  if (index < 0) return null;
  const target = ordered[index];
  const previous = ordered[index - 1];
  if (!target.endsOn) {
    return previous
      ? `Deleting this leaves ${readable(previous.start_date)} onward priced by the set before it.`
      : 'Deleting this leaves no rules, so net will equal gross again.';
  }
  if (!previous) {
    return `Deleting this leaves ${readable(target.start_date)} to ${readable(target.endsOn)} with no rules, so those orders will net their full amount.`;
  }
  return `This will re-price ${readable(target.start_date)} to ${readable(target.endsOn)} using the ${readable(previous.start_date)} rules.`;
};

/**
 * Reasons the server would refuse, caught early so saving is not a guess.
 *
 * Each names the row it is about. An earlier version described the rule set by
 * its date — "a charge in 2025-01-01 has no amount" — which told the reader
 * neither which charge nor where to look, and sat next to the preview's own
 * failure in matching red. Naming the charge is the difference between a
 * complaint and an instruction.
 */
export const localProblems = (sets) => {
  const problems = [];
  const starts = new Set();
  const many = sets.length > 1;
  sets.forEach((set, index) => {
    const where = many
      ? ` in the rules from ${trimmed(set.start_date) ? readable(set.start_date) : `set ${index + 1}`}`
      : '';
    if (!trimmed(set.start_date)) problems.push(`Rule set ${index + 1} needs a start date.`);
    else if (starts.has(set.start_date)) problems.push(`Two rule sets start on ${readable(set.start_date)}.`);
    else starts.add(set.start_date);
    set.perOrder.forEach((rule, position) => {
      if (!trimmed(rule.amount)) problems.push(`Charge ${position + 1}${where} needs an amount.`);
    });
    set.period.forEach((rule, position) => {
      if (!trimmed(rule.amount)) problems.push(`Recurring charge ${position + 1}${where} needs an amount.`);
      if (!(Number(trimmed(rule.every_days)) > 0)) {
        problems.push(`Recurring charge ${position + 1}${where} needs a number of days.`);
      }
    });
  });
  return [...new Set(problems)];
};
